// src/components/Layout/ResponsiveSidebarWrapper.js
import React, { useEffect, useState } from "react";
import { Box, Drawer, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useLocation } from "react-router-dom";
import Sidebar from "./Sidebar";
import TopBar from "./TopBar";

const ResponsiveSidebarWrapper = ({ children }) => {
  const theme = useTheme();
  const location = useLocation();

  const isMobileWidth = useMediaQuery(theme.breakpoints.down("lg"));
  const isMobileHeight = useMediaQuery("(max-height: 500px)");
  const isMobile = isMobileWidth || isMobileHeight;
  const isTeamChat = location.pathname === "/team-chat";

  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (isMobile) setMobileOpen(false);
  }, [location.pathname, isMobile]);

  const handleDrawerToggle = () => setMobileOpen((prev) => !prev);

  const isLoginPage = location.pathname === "/login";
  const isForgotPasswordPage = location.pathname === "/forgot-password";
  const isResetPasswordPage = location.pathname.startsWith("/reset-password");
  const isThankYouPage =
    location.pathname === "/thank-you/proposal" ||
    location.pathname === "/thank-you/proposal-unavailable";
  const isRegisterPage = location.pathname === "/register";
  const isCompanySignupPage = location.pathname === "/signup/company";

  const isPublicPage =
    isLoginPage ||
    isForgotPasswordPage ||
    isResetPasswordPage ||
    isThankYouPage ||
    isRegisterPage ||
    isCompanySignupPage;

  if (isPublicPage) {
    return <>{children}</>;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: { xs: "100dvh", lg: "100vh" },
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <TopBar onMenuClick={handleDrawerToggle} />

      <Box
        sx={{
          display: "flex",
          width: "100%",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {isMobile ? (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: "block", lg: "block" },
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: 260,
                pt: "env(safe-area-inset-top, 0px)",
                pb: "env(safe-area-inset-bottom, 0px)",
                height: "100dvh",
                maxHeight: "100dvh",
                display: "flex",
                flexDirection: "column",
              },
            }}
          >
            <Sidebar />
          </Drawer>
        ) : (
          <Box
            sx={{
              width: 260,
              flexShrink: 0,
              height: "100%",
              borderRight: "1px solid rgba(255,255,255,0.08)",
              overflowY: "auto",
              overflowX: "hidden",
              position: "relative",
            }}
          >
            <Sidebar />
          </Box>
        )}

        <Box
          component="main"
          sx={{
            flex: 1,
            flexGrow: 1,
            minWidth: 0,
            minHeight: 0,
            maxWidth: "100%",
            boxSizing: "border-box",
            mt: {
              xs: "var(--topbar-height, calc(64px + env(safe-area-inset-top, 0px)))",
              lg: 8,
            },
            px: isTeamChat ? { xs: 0, lg: 2 } : 2,
            pt: isTeamChat ? { xs: 0, lg: 2 } : 2,
            pb: isTeamChat ? { xs: 0, lg: 2 } : 0,
            bgcolor: isTeamChat ? { xs: "#fff", lg: "#f5f7fa" } : undefined,
            overflowY: isTeamChat ? "hidden" : "auto",
            overflowX: "hidden",
            WebkitOverflowScrolling: "touch",
            display: isTeamChat ? "flex" : "block",
            flexDirection: "column",
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default ResponsiveSidebarWrapper;
